import type { ButtonHTMLAttributes, InputHTMLAttributes, ReactNode } from 'react'

export type ButtonVariant = 'primary' | 'secondary' | 'danger'

/** Shared class names for buttons and button-styled links (avoid nested interactive elements). */
export function buttonClassName(variant: ButtonVariant = 'primary', className = ''): string {
  const styles =
    variant === 'primary'
      ? 'bg-[var(--brand-navy)] text-white hover:bg-slate-800'
      : variant === 'danger'
        ? 'bg-red-700 text-white hover:bg-red-800'
        : 'border border-slate-300 bg-white text-slate-800 hover:bg-slate-50'
  return `inline-flex items-center justify-center rounded-lg px-4 py-2 text-sm font-medium disabled:cursor-not-allowed disabled:opacity-50 ${styles} ${className}`
}

export function Card({ children, className = '' }: { children: ReactNode; className?: string }) {
  return (
    <div className={`rounded-xl border border-slate-200 bg-white p-5 shadow-sm ${className}`}>
      {children}
    </div>
  )
}

export function Title({ children }: { children: ReactNode }) {
  return <h1 className="mb-1 text-2xl font-semibold text-slate-900">{children}</h1>
}

export function Subtitle({ children }: { children: ReactNode }) {
  return <p className="mb-4 text-sm text-slate-600">{children}</p>
}

export function Label({ htmlFor, children }: { htmlFor: string; children: ReactNode }) {
  return (
    <label htmlFor={htmlFor} className="mb-1 block text-sm font-medium text-slate-700">
      {children}
    </label>
  )
}

export function Input(props: InputHTMLAttributes<HTMLInputElement>) {
  const { className = '', ...rest } = props
  return (
    <input
      className={`w-full rounded-lg border border-slate-300 px-3 py-2 text-base text-slate-900 outline-none focus:border-[var(--brand-navy)] focus:ring-2 focus:ring-[var(--brand-navy)]/20 ${className}`}
      {...rest}
    />
  )
}

export function Button({
  variant = 'primary',
  className = '',
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: ButtonVariant }) {
  return <button className={buttonClassName(variant, className)} {...props} />
}

export function Alert({
  tone = 'error',
  children,
  className = '',
}: {
  tone?: 'error' | 'info' | 'success'
  children: ReactNode
  className?: string
}) {
  const styles =
    tone === 'error'
      ? 'border-red-200 bg-red-50 text-red-800'
      : tone === 'success'
        ? 'border-emerald-200 bg-emerald-50 text-emerald-900'
        : 'border-sky-200 bg-sky-50 text-sky-900'
  return (
    <div
      role={tone === 'error' ? 'alert' : 'status'}
      className={`rounded-lg border px-3 py-2 text-sm ${styles} ${className}`}
    >
      {children}
    </div>
  )
}

export function FieldError({ children }: { children?: ReactNode }) {
  if (!children) return null
  return <p className="mt-1 text-sm text-red-700">{children}</p>
}
